import ScheduleLocationMapping from "./ScheduleLocationMapping";
import {Schedule, Service, Shift, Settings, StopInfo, Address} from "@esWebAPI/es/model/Schedule";

import type { FeatureViewModel } from "./FeatureViewModel";

import dayjs from "dayjs";
import _ from "lodash";

export default class ScheduleModel {
    SHORT_LIST_COLOR = 4294554432;
    INACTIVE_COLOR = 4291611852;

    private readonly id: number;
    private settings:Settings|null = null;
    private shifts: Shift[] = [];
    private services: Service[] = [];
    numRemainingServices = 0;
    activeRecommendations = {};

    private readonly locationMapping: ScheduleLocationMapping;
    private timeFrame: number[] = null;

    loadComplete = $.Deferred();

    private readonly eventChannel = es.util.createEventChannel({
        onSchedulePointMappingUpdate: 1,
        onTimeFrameChange: 1,
        onColorsChange: 1
    }, "ScheduleModel");

    private readonly maxRoutingDaysPerSchedule: number;
    private readonly maxStopsDaySchedule: number;
    private readonly maxStopsWeekSchedule: number;
    private readonly maxStopsMultiWeekSchedule: number;

    /**
     *
     * @param schedule whole schedule entity (see: com.espatial.es.model.schedule.Schedule)
     * @param featureViewModel
     * @param tenantLimits
     * @param tenantConfig
     */
    constructor(schedule: Schedule, featureViewModel: FeatureViewModel, tenantLimits: any, tenantConfig: any) {
        this.id = schedule.id;
        this.maxRoutingDaysPerSchedule = tenantLimits.maxRoutingDaysPerSchedule;
        this.maxStopsDaySchedule = tenantConfig.maxStopsDaySchedule ?? 50;
        this.maxStopsWeekSchedule = tenantConfig.maxStopsWeekSchedule ?? 125;
        this.maxStopsMultiWeekSchedule = tenantConfig.maxStopsMultiWeekSchedule ?? 300;
        this.locationMapping = new ScheduleLocationMapping(featureViewModel, this.eventChannel);

        this.reloadSettings(schedule.hasOwnProperty("settings") ? schedule.settings : this.getDefaultSettings());
        this.reloadShifts(schedule.hasOwnProperty("shifts") ? schedule.shifts : []);
        this.reloadServices(schedule.hasOwnProperty("services") ? schedule.services : []);
        this.numRemainingServices = this.calculateNumRemainingServicesAvailable();
        this.reloadActiveRecommendations(schedule.hasOwnProperty("activeRecommendations") ? schedule.activeRecommendations : {});
        
        this.loadComplete.resolve();
    }

    public reload(schedule: Schedule){
        this.reloadSettings(schedule.hasOwnProperty("settings") ? schedule.settings : this.getDefaultSettings());
        this.reloadShifts(schedule.hasOwnProperty("shifts") ? schedule.shifts : []);
        this.reloadServices(schedule.hasOwnProperty("services") ? schedule.services : []);
    }

    public reloadShifts(shifts: Shift[]){
        this.shifts = es.util.object.deepCopy(shifts);
        this.numRemainingServices = this.calculateNumRemainingServicesAvailable();
    };

    public reloadServices(services: Service[]){
        this.services = es.util.object.deepCopy(services);
        this.numRemainingServices = this.calculateNumRemainingServicesAvailable();
        this.locationMapping.reload(this.services);
        /*
        debugger;
        const tzOffset = new Date().getTimezoneOffset();
        services.forEach(s => {
            if(this.isServiceScheduledInSolution(s)){
                s.stopInfo.arrTime = dayjs.unix(s.stopInfo.arrTime).utcOffset(tzOffset, true).unix();
                s.stopInfo.endTime = dayjs.unix(s.stopInfo.arrTime).utcOffset(tzOffset, true).unix();
            }
        });
        */
        // check for duplicate ids
        if(services.length !== new Set(services.map(s => s.id)).size){
            console.error("reloadServices : illegalState : found duplicate ids in services: " + services.map(s => s.id).sort());
        }
    }

    public reloadSettings(settings: Settings){
        this.settings = es.util.object.deepCopy(settings);
    }
    
    public reloadActiveRecommendations(activeRecommendations: any) {
        this.activeRecommendations = es.util.object.deepCopy(activeRecommendations);
    }

    public getId(): number {
        return this.id;
    }

    public isScheduleGeoMap(): boolean {
        return this.id !== null;
    }

    /**
     * 
     * @returns {boolean}
     */
    public hasSolution(): boolean {
        return this.services && this.services.some(s => this.isServiceStop(s) && !this.isServiceBreak(s));
    }

    /**
     * Checks if the settings should be locked.
     * Settings will be locked if there are any services present in the calendar.
     */
    public areSettingsLocked(): boolean {
        return this.services && this.services.some(s => s.overrideStartTime != undefined || this.isServiceStop(s))
    }

    /**
     * Calculates the number of Services that still can be added to the Schedule
     * @returns {number}
     */
    calculateNumRemainingServicesAvailable(): number {
        return this.getServicesLimit() - this.getStopServicesObjRef().length;
    }

    /**
     * Get the number of services that the user can add to the shortlist/schedule
     * @returns {number}
     */
    getNumRemainingServices() {
        return this.numRemainingServices;
    }

    public getServicesLimit(): number {
        const shiftCount = this.shifts.length || Math.min(this.maxRoutingDaysPerSchedule, 5);
        let maxStops;
        if (shiftCount === 1) {
            maxStops = this.maxStopsDaySchedule;
        } else if(shiftCount <= 7) {
            maxStops = this.maxStopsWeekSchedule;
        } else {
            maxStops = this.maxStopsMultiWeekSchedule;
        }
        return maxStops;
    }

    getSettingsClone() {
        return es.util.object.deepCopy(this.settings);
    }

    getSettingsObjRef() {
        return this.settings;
    }

    getServicesClone(): any[] {
        return es.util.object.deepCopy(this.services);
    }

    getServicesObjRef(): any[] {
        return this.services;
    }

    getStopServicesObjRef(): any[] {
        return this.services.filter(s => this.isUserService(s));
    }

    getShiftsClone(): any[] {
        return es.util.object.deepCopy(this.shifts);
    }

    getShiftsObjRef(): any[] {
        return this.shifts;
    }

    public getEventChannel() {
        return this.eventChannel;
    }

    public getServiceId(layerViewId: number, featureId: number, name: string, stopInfo: StopInfo): string {
        if (name && name.startsWith("es_lunch-")){
            return `_${name}`;
        }
        if (stopInfo && stopInfo.type !== "STOP") {
            return `_${stopInfo.shiftId}_${stopInfo.type.toLowerCase()}`
        }
        return `${layerViewId}_${featureId}`;
    }

    getNextShiftId(): string {
        return "shift_" + (this.shifts.length + 1);
    }

    /**
     * Add a service to the schedule.
     *
     * @param layerViewId associated feat lvId
     * @param featureId associated feat id
     * @param name service title, also used as label
     * @param duration Specifies the duration of the service in seconds, i.e. how long it takes at the customer site.
     * @param address (optional) gh address obj
     * @param overrideStartTime (optional)
     * @param stopInfo (optional) pre-defined stop info for start / end services
     * @param locked (optional) if the stop is locked
     * @returns {{duration: (*|number), name, id: string}|null}
     */
    public addService(layerViewId: number, featureId: number, name = "", duration: number, address = null, overrideStartTime = null, stopInfo:StopInfo|null = null, locked = false) {
        const mockService = {name, stopInfo};
        if(this.numRemainingServices === 0 && this.isUserService(mockService)){
            let msg = $i18n('_Unscheduled_Max_Services', [this.getServicesLimit(), this.shifts.length, 1, this.getStopServicesObjRef().length]);
            es.alert.alertOk(msg);
            return null;
        }

        if(name === null){
            name = "";
        }

        const isStartEnd = stopInfo && (stopInfo.type === "START"  || stopInfo.type === "START_END" || stopInfo.type === "END");

        const duplicatedService = this.services.find(s => s.layerViewId === layerViewId && s.featureId === featureId);
        const isFeatureAlreadyInServices =  duplicatedService !== undefined;
        if(isFeatureAlreadyInServices && !isStartEnd) {
            console.log("Feature already in the model, skipping", layerViewId, featureId, name);
            return duplicatedService;
        }

        const id = this.getServiceId(layerViewId, featureId, name, stopInfo)
        const index = this.services.findIndex(s => s.id === id);
        if (index > -1) {
            // throw new Error
            console.error(`Cannot add service to model as service with same id already exists ${id} (${name}) ... skipping for now (TODO: update or throw err)`, this.services);
            return null;
        }
        // [138_10, Mosley Inc., {"lat":28.07929,"locationId":"138_10","lon":-80.67593,"name":"Mosley Inc."}, 0, 2, null, null, null]

        const service = {
            id: id,
            layerViewId: parseInt(layerViewId),
            featureId: parseInt(featureId),
            name: name,
            duration: duration
        };

        if (address) {
            service.address = address;
        }

        if (overrideStartTime) {
            service.overrideStartTime = overrideStartTime;
            service.locked = locked
        }

        if (stopInfo) {
            service.stopInfo = stopInfo;
        }

        this.validateService(service);

        this.services.push(service);

        if (this.isUserService(service)) {
            this.numRemainingServices--;
        }

        this.locationMapping.add([service]);
        
        return es.util.object.deepCopy(service);
    }

    /**
     * Validate service do it doesn't fail marshalling on server where it's harder to track the origin of the bug.
     * @param s
     */
    public validateService(s: Service) {
        if (isNaN(parseInt(s.layerViewId)) || isNaN(parseInt(s.featureId)) || parseInt(s.featureId) < 0) {
            throw new Error(`Invalid lvId or featId passed [${s.layerViewId}], [${s.featureId}]`);
        }

        if (s['overrideStartTime'] && typeof s['overrideStartTime'] !== 'number') {
            throw new Error("Invalid overrideStartTime set: " + s.overrideStartTime);
        }
    }

    public editService(service: Service) {
        // We WANT type coercion here. just to be on the safe side.
        const index = this.services.findIndex(s => s.id == service.id);
        if(index < 0){
            throw new Error(`Could not find service with id ${service.id} (${service.name})`);
        }
        this.validateService(service);
        this.services[index] = es.util.object.deepCopy(service);
    }

    public removeService(id: string) {
        //console.log("removeService", this.getService(id));
        const service = this.getService(id);
        this.locationMapping.remove(service);

        if(typeof id !== "string") {
            console.warn("Id passed is not a string. Service Ids should be strings.")
            id = id.toString();
        }

        this.services = this.services.filter(s => s.id !== id);
        if(this.isUserService(service)) {
            this.numRemainingServices++;
        }
    }

    /**
     * Remove unscheduled services only
     * (from app PoV, clears the unscheduled panel and leaves the scheduled services in panel untouched)
     */
    removeUnScheduledServices() {
        this.services.forEach(s => {
            if (this.isServiceUnScheduled(s)) {
                this.removeService(s.id);
            }
        });
    }

    /**
     * Remove all services
     */
    removeAllServices() {
        this.services = [];
        this.locationMapping.clear();
    }

    /**
     * 
     * @param shifts 
     */
    public addShifts(shifts: Shift[]) {
        if(!shifts || shifts.length === 0){
            // throw new Error would get swallowed by promise chain
            console.error("Attempted to add null or empty shifts");
        }

        for(let i = 0; i < shifts.length; i++){
            const s = shifts[i];
            this.addShift(s.startAddress, s.endAddress, s.earliestStart, s.latestEnd, s.locked, s.returnToDepot);
        }
    }

    /**
     *
     * @param startAddress gh Address obj Note: can use featKey either see: #addShiftWithFeatKey
     * @param endAddress gh Address obj   Note: can use featKey either see: #addShiftWithFeatKey
     * @param earliestStart recommended Unix time stamp (the number of seconds since 1970-01-01) or for example,
     *                      you count the seconds from Monday morning 00:00 and define the whole week in seconds.
     *                      For example, Monday 9am is then represented by 9hour * 3600sec/hour = 32400sec. In turn,
     *                      Wednesday 1pm corresponds to 2day * 24hour/day * 3600sec/hour + 1day * 13hour/day * 3600sec/hour = 219600sec.
     * @param latestEnd
     * @param locked ES property to state if the shift is to send to gh request
     * @param returnToDepot If it is false, the algorithm decides where to end the vehicle route. It ends in one of your customers' locations
     */
    public addShift(startAddress: Address|undefined, endAddress: Address|undefined, earliestStart: number, latestEnd: number, locked = false, returnToDepot = false) {
        if (returnToDepot && !startAddress) {
            // throw new Error would get swallowed by promise chain
            console.error("returnToDepot is true but no startAddress was specified", startAddress);
        }

        const shift= {
            shiftId: this.getNextShiftId(),
            earliestStart: earliestStart,
            latestEnd: latestEnd,
            locked,
            returnToDepot
        } as Shift;

        if  (startAddress){
            shift.startAddress = startAddress;
        }
        if(endAddress){
            shift.endAddress = endAddress;
        }

        this.shifts.push(shift);
    }

    public editShift(shift: Shift){
        const index = this.shifts.findIndex(s => s.shiftId === shift.shiftId);
        if(index < 0){
            throw new Error(`Could not find shift with id ${shift.shiftId} (${JSON.stringify(shift)})`);
        }
        this.shifts[index] = es.util.object.deepCopy(shift);
    }

    removeShift(shiftId: string) {
        this.shifts = this.shifts.filter(s => s.shiftId !== shiftId);
    }

    clearShifts() {
        this.shifts = [];
    }

    public getDefaultSettings(): Settings {
        const nextMonday = new Date();
        nextMonday.setDate(nextMonday.getDate() + (7 - nextMonday.getDay()) % 7 + 1);
        
        const startDate = dayjs(nextMonday).startOf('D').unix();
        const days = Math.min(this.maxRoutingDaysPerSchedule, 5);
        const finishDate = days == 1 ? startDate : dayjs(new Date(startDate * 1000)).add(days - 1, "days").unix();

        return {
            startAddress: undefined,
            endAddress: undefined,
            travelMode: "car",
            roundTrip: false,
            frequency: "daily",
            startDate: startDate,
            finishDate: finishDate,
            shiftStartTime: 9 * 60 * 60,
            shiftFinishTime: 17.5 * 60 * 60,
            breakEarliestStartTime: 13 * 60 * 60,
            breakLatestStartTime: 14 * 60 * 60,
            breakDuration: 60 * 60,
            stopDuration: 30 * 60,
            dayAvailability: "1111100", //  Monday to Sunday
            stopTimeFieldMapping: new Map<number, string>(),
            colors: es.util.object.deepCopy(es.util.color.palettes["SCHEDULE_DEFAULT_COLORS"]).map((c: string) => es.util.color.serverFromHex(c))
        } as Settings;
    }
    
    public getLocationMappingObjRef() {
        return this.locationMapping;
    }

    public getShift(shiftId: string) {
        return this.shifts.find(s => shiftId === s.shiftId);
    }

    public getService(serviceId: string) {
        return this.services.find(s => s.id === serviceId)
    }

    /**
     * Set the timeframe currently viewing
     * @param timeFrame Array of 2 timestamps: start, end
     */
    public setTimeFrame(timeFrame: number[]) {
        this.timeFrame = timeFrame;
        this.eventChannel.fireEvent("onTimeFrameChange", timeFrame);
    }

    public  getTimeFrame() {
        return this.timeFrame;
    }

    public getTimeFrame_debug() {
        return new Date(this.timeFrame[0]).toUTCString() + " to " + new Date(this.timeFrame[1]).toUTCString();
    }

    /**
     * Is shiftId within the current model timeframe
     * NOTE: current model timeframe can be either a week or a day so there may be more than one shift active
     * @param shiftId
     * @returns {boolean}
     */
    public isShiftActive(shiftId: String): boolean {
        for (let shift of this.shifts) {
            if (shift.shiftId === shiftId && this.timeFrame && shift.earliestStart >= this.timeFrame[0] / 1000 && shift.latestEnd <= this.timeFrame[1] / 1000){
                return true;
            }
        }
        return false;
    }

    /**
     * Get the shift that represents a particular day
     * @param date Javascript Date Object
     * @returns {shift}
     */
    getShiftByDay(date: Date) {
        const dayjsDate = dayjs(date);
        return _.find(this.shifts, (s) => dayjs.unix(s.earliestStart).isSame(dayjsDate, "day") );
    }

    /**
     * Convenience util method to check if a service is in schedule solution (i.e. has a stopInfo).
     *
     * May need to expand on this if we want to include detail on services that did make it into the solution but where
     * 'locked' or had an overrideStartTime (i.e. was explicitly added to the planner).
     *
     * Note: see java equivalent EsService#isServiceScheduledInSolution - the two should not get out of sync or there'll be discrepancies
     *
     * @param service
     */
    isServiceScheduledInSolution(service: Service): boolean {
        return service?.stopInfo?.type === "STOP";
    }

    /**
     * Check if a service is scheduled and the associated shift is locked
     * 
     * @param service
     * 
     * @returns {false|boolean|*}
     */
    isServiceOrShiftLocked(service: Service): boolean {
        return this.isServiceLocked() || ((service.stopInfo != undefined && this.getShift(service.stopInfo.shiftId).locked));
    }

    /**
     * Check if service is start, end or start_end.
     * Note: see java equivalent EsService#isServiceStartEnd - the two should not get out of sync or there'll be tricky
     *       discrepancies between client and server logic.
     * 
     * @param service
     * 
     * @returns {boolean}
     */
    isServiceAnyStartOrEnd(service: Service): boolean {
        return ["START", "END", "START_END"].includes(service?.stopInfo?.type);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceStartEndOnly(service: Service): boolean {
        return ["START_END"].includes(service?.stopInfo?.type);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceStartOnly(service: Service): boolean {
        return ["START"].includes(service?.stopInfo?.type);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceEndOnly(service: Service): boolean {
        return ["END"].includes(service?.stopInfo?.type);
    }

    // FIXME :: This only works after the schedule has been created
    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceStop(service: Service): boolean {
        return ["STOP"].includes(service?.stopInfo?.type);
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isUserService(service: Service): boolean {
        return !this.isServiceAnyStartOrEnd(service) && !this.isServiceBreak(service)
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceLocked(service: Service): boolean {
        return service && service.locked;
    }

    /**
     * Convenience util method to check if a service is in the planner but does NOT have a schedule solution (i.e. has a
     * overrideStartTime but does not have a stopInfo).
     *
     * Can be used to determine whether a service belongs in an 'unscheduled' list.
     *
     * Note: see java equivalent EsService#isServiceUnScheduled - the two should not get out of sync or there'll be discrepancies
     * 
     * TODO: rename isServiceScheduled (negative naming)? replace here and in java
     * 
     * @param service
     * 
     * @returns 
     */
    public isServiceUnScheduled(service: Service): boolean {
        return !this.isServiceScheduledInSolution(service) &&
            !this.isServiceBreak(service) &&
            !this.isServiceAnyStartOrEnd(service) &&
            !this.isServiceLocked(service) &&
            service.overrideStartTime == undefined;
    }

    /**
     * Checks if service is scheduled for some time, whether it is in a schedule or has an override start time
     * i.e. this basically indicates if a schedule appears in the calendar.
     * 
     * @param service 
     * 
     * @returns 
     */
    public isServiceScheduledOrHasStartTime(service: Service): boolean {
        return this.isServiceScheduledInSolution(service) || (this.getServiceStartTime(service) ?? 0) > 0;
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceScheduled(service: Service): boolean {
        return this.isServiceScheduledInSolution(service) &&
            ( this.isServiceAnyStartOrEnd(service) || this.isServiceStop(service) );
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    isServiceBreak(service: Service): boolean {
        return service && service.hasOwnProperty("name") && service.name !== null && service.name.startsWith("es_lunch-");
    }

    /**
     * 
     * @param service 
     * 
     * @returns 
     */
    public getServiceStartTime(service: Service) {
        if (!service) {
            return null;
        }

        if (service.stopInfo) {
            return service.stopInfo.arrTime + service.stopInfo.waitingTime;
        }

        if (service.overrideStartTime) {
            return service.overrideStartTime;
        }

        return null;
    }

    /**
     * 
     * @param index 
     * @param color 
     * @param isSuppressEvent 
     */
    setColor(index: number, color: number, isSuppressEvent?: boolean) {
        this.settings.colors[index] = color;

        if(isSuppressEvent !== true) {
            this.eventChannel.fireEvent("onColorsChange", this.settings.colors);
        }
    }

    /**
     * 
     * @param index 
     * 
     * @returns 
     */
    getColor(index: number) {
        return this.settings.colors[index];
    }

    /**
     * 
     * @param costPerDistance 
     */
    setCostPerDistance(costPerDistance) {
        this.settings.costPerDistance = costPerDistance;
    }

    /**
     * 
     * @param shiftId 
     * 
     * @returns 
     */
    getShiftColor(shiftId: string) {
        const shift = this.getShift(shiftId);
        const day = new Date(shift.earliestStart * 1000).getDay(); // off by one!
        const color = this.getColor(day);
        //es.log.log("getShiftColor", shiftId, day, color);
        //es.util.color._debug(color);
        return color;
    }

    /**
     * 
     * @returns 
     */
    getInactiveColor() {
        return this.INACTIVE_COLOR;
    }

    /**
     * 
     * @returns 
     */
    getShortListColor() {
        return this.SHORT_LIST_COLOR;
    }

    /**
     * Return services clone with props (just dates and times for now. may expand later) in a human-readable format.
     */
    getServices_debug(services: Service[]) {
        if(!services){
            services = this.getServicesClone();
        }
        services.forEach(s =>{
            if(s.hasOwnProperty("overrideStartTime")){
                s.overrideStartTime = s.overrideStartTime + " (" + new Date(s.overrideStartTime * 1000).toLocaleString() + ")";
            }
            if(s.stopInfo){
                s.stopInfo.arrTime = s.stopInfo.arrTime + " (" + new Date(s.stopInfo.arrTime * 1000).toLocaleString() + ")";
                s.stopInfo.endTime = s.stopInfo.endTime + " (" + new Date(s.stopInfo.endTime * 1000).toLocaleString() + ")";
                s.stopInfo.distance = s.stopInfo.distance + " (" + (s.stopInfo.distance / 1000) + "kms)"; // 'cumulated distance from start to this activity in m'
                s.stopInfo.drivingTime = s.stopInfo.drivingTime + " (" + es.util.date.secondsToText(s.stopInfo.drivingTime) + ")"; // 'cumulated driving time from start to this driver activity in seconds'
                s.stopInfo.waitingTime = s.stopInfo.waitingTime + " (" + es.util.date.secondsToText(s.stopInfo.waitingTime) + ")"; // 'Waiting time at this activity in seconds. A waiting time can occur if the activity has at least one time window. If arr_time < time_window.earliest a waiting time of time_window_earliest - arr_time occurs.'
            }
            //"time_windows":[{"earliest":1682940732,"latest":1682940732}]
            
            if(s.hasOwnProperty("time_windows")){ // TODO: this is gh rq, support es model i.e. timeWindows
                s.time_windows.forEach(tw => {
                    tw.earliest = tw.earliest + " (" + new Date(tw.earliest * 1000).toLocaleString() + ")";
                    tw.latest = tw.latest + " (" + new Date(tw.latest * 1000).toLocaleString() + ")";
                });
                s.time_windows = JSON.stringify(s.time_windows);
            }
            s.address = JSON.stringify(s.address);
            s.duration = s.duration + " (" + (s.duration / 60) + " mins)"; // duration is in seconds
        });
        return services;
    }

    /**
     * 
     * @param shifts 
     * @returns 
     */
    getShifts_debug(shifts: Shift[]) {
        if(!shifts){
            shifts = this.getShiftsClone();
        }
        shifts.forEach(s =>{
            s.earliestStart = s.earliestStart + " (" + new Date(s.earliestStart * 1000).toLocaleString() + ")";
            s.latestEnd = s.latestEnd + " (" + new Date(s.latestEnd * 1000).toLocaleString() + ")";
        });
        return shifts;
    }

    /**
     * 
     * @param settings 
     * @returns 
     */
    getSettings_debug(settings: Settings) {
        if(!settings){
            settings = this.getSettingsClone();
        }
        /*
            {
              "breakDuration": 3600,
              "breakEarliestStartTime": 46800,
              "breakLatestStartTime": 50400,
              "colors": [4278737010,4283612323,4291018262,4284311985,4291584432,4280580460,4289047193],
              "dayAvailability": "1000000",
              "finishDate": 0,
              "frequency": "daily",
              "roundTrip": false,
              "shiftFinishTime": 63000,
              "shiftStartTime": 32400,
              "startDate": 0,
              "stopDuration": 1800,
              "stopTimeFieldMapping": {},
              "travelMode": "car"
            }
        */
        settings.breakDuration = settings.breakDuration + " (" + es.util.date.secondsToText(settings.breakDuration) + ")";
        settings.breakEarliestStartTime = settings.breakEarliestStartTime + " (" + es.util.date.secondsToText(settings.breakEarliestStartTime) + ")";
        settings.breakLatestStartTime = settings.breakLatestStartTime + " (" + es.util.date.secondsToText(settings.breakLatestStartTime) + ")";

        settings.shiftStartTime = settings.shiftStartTime + " (" + es.util.date.secondsToText(settings.shiftStartTime) + ")";
        settings.shiftFinishTime = settings.shiftFinishTime + " (" + es.util.date.secondsToText(settings.shiftFinishTime) + ")";

        settings.stopDuration = settings.stopDuration + " (" + es.util.date.secondsToText(settings.stopDuration) + ")";

        return settings;
    }
    
    /**
     * 
     * @param layerMetaId 
     * @returns 
     */
    getActiveRecommendation(layerMetaId: number) {
        return this.activeRecommendations[layerMetaId];
    }
    
    /**
     * 
     * @param layerMetaId 
     */
    removeActiveRecommendation(layerMetaId: number) {
        delete this.activeRecommendations[layerMetaId];
    }
    
    /**
     * 
     * @param layerMetaId 
     * @param recommendationId 
     */
    setActiveRecommendation(layerMetaId: number, recommendationId: number) {
        this.activeRecommendations[layerMetaId] = recommendationId;
    }
}
